// Legacy "old cleverly" utility tokens — the SAME key set as ./utility.ts
// (which component styles reference via var(--p-*)), but with the pre-rebrand
// blue-brand values (restored from git 49300725a^). Any key that did not exist
// pre-rebrand (shadowxs–lg, shadowpop, boxshadowbrandsoft, …) uses the closest
// old-brand equivalent so switching presets leaves no orange remnants.
export const legacyUtility = {

    // ===== NEUTRAL SCALE =====
    // ===== ROLE TOKENS (see utility.ts §9 contract) — legacy blue-brand values =====
    textheading: '{gray.900}',
    textaccent: '{primary.600}',
    textondark: '#FFFFFF',
    textneutral: '{gray.900}',             // neutral-darkest: secondary/link button + tag labels only
    surfacepage: '{gray.50}',
    surfacecream: '{yellow.100}',
    surfacesoft: '{blue.50}',
    surfaceaccent: '{blue.100}',
    surfacepeach: '{red.50}',
    surfacesubtle: 'rgba(0, 0, 0, .05)',
    surfacedark: '{gray.900}',
    bordercontrol: '{gray.400}',
    borderselected: '{blue.500}',

    // ===== TEXT =====
    textdisabled: 'rgba(0, 0, 0, 0.30)',

    textcolordisabled: 'rgba(0, 0, 0, .30)',

    inputbordercolorfocus: '{blue.500}',
    inputbackgrounddisabled: 'rgba(0, 0, 0, .10)',

    // ===== STATE COLORS =====
    statesuccess: '{green.400}',
    statesuccessbg: '{green.100}',
    stateerror: '{red.400}',
    stateerrorbg: '{red.100}',
    statewarning: '{orange.500}',
    statewarningbg: '{yellow.200}',
    stateinfo: '{blue.600}',
    stateinfobg: '{blue.100}',

    // ===== BOX SHADOWS =====
    boxshadowlayer1: '0 4px 16px rgba(0, 0, 0, 0.12), 0 20px 160px rgba(0, 0, 0, 0.12)',

    fontfamily: '"Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',
    headingfontfamily: '"Poppins", "Inter", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Helvetica, Arial, sans-serif',

    buttonheight: '44px',
    buttonfontweight: '600',
    buttonlinkcolor: '{primary.600}',
    buttonlinkhovercolor: '{primary.700}',

    // Secondary button: the legacy theme keeps its filled ink look (the values
    // button.ts used before O14) — see utility.ts for the brand-guide variant.
    buttonsecondarybackground: '{sand.950}',
    buttonsecondaryhoverbackground: '{grey.800}',
    buttonsecondaryactivebackground: '{grey.700}',
    buttonsecondarybordercolor: '{sand.950}',
    buttonsecondaryhoverbordercolor: '{grey.800}',
    buttonsecondaryactivebordercolor: '{grey.700}',
    buttonsecondarycolor: '#FFFFFF',
    // Surface buttons: legacy keeps transparent outlined/raised-text buttons
    buttonsurface: 'transparent',
    buttonsurfaceborder: 'transparent',
    buttonsurfaceshadow: '0 0 #0000',

    // Shared button state rules (cleverly-overrides.scss): legacy = 2px border,
    // no hover lift, press scale, PrimeNG raised shadow kept.
    buttonborderwidth: '2px',
    buttonhovertransform: 'none',
    buttonhovershadow: 'none',
    buttonactivetransform: 'scale(.96)',
    buttonactiveshadow: 'none',
    buttonraisedhovershadow: '0 3px 1px -2px #0003, 0 2px 2px #00000024, 0 1px 5px #0000001f',
    buttonraisedactiveshadow: '0 3px 1px -2px #0003, 0 2px 2px #00000024, 0 1px 5px #0000001f',
    badgebackground: '{red.500}',   // legacy danger badge
    badgecolor: '#FFFFFF',
    badgering: '0px',
    badgeshadow: '0 0 #0000',   // no shadow (valid in a shadow list)
    // cl-tag: legacy keeps the pre-sync look (brand pill, white label).
    tagbackground: '{primary.color}',
    tagcolor: '#FFFFFF',
    tagradius: '100px',
    tagfontsize: '0.75rem',
    tagfontweight: '500',
    tagpillbackground: '{surface.100}',
    tagpillcolor: '{primary.color}',
    tagpillborder: 'transparent',
    tagpillradius: '100px',
    tagaltbackground: '#FFFFFF',
    tagaltcolor: '{primary.color}',
    buttonsecondaryaltbackground: '{sand.950}',
    buttonsecondaryalthoverbackground: '{grey.800}',
    buttonsecondaryaltbordercolor: '{sand.950}',
    buttonsecondaryaltcolor: '#FFFFFF',
    buttonheightlarge: '3.25rem',

    inputheight: '44px',

    buttonpaddingvertical: '14px',
    buttonpaddinghorizontal: '20px',

    inputpaddingx: '16px',
    inputpaddingy: '12px',

    headingmarginblockstart: '0',
    headingmarginblockend: '0',
    headingmargininlinestart: '0',
    headingmargininlineend: '0',

    maskbg: 'rgba(0, 0, 0, 0.4)',

    // ===== ELEVATION / MOTION (old-brand equivalents) =====
    shadowsm: '0 2px 6px rgba(0, 0, 0, .12), 0 1px 2px rgba(0, 0, 0, .10)',
    shadowmd: '0 4px 16px rgba(0, 0, 0, .12), 0 20px 160px rgba(0, 0, 0, .12)',
    shadowlg: '0 4px 24px rgba(0, 0, 0, .18), 0 12px 48px rgba(0, 0, 0, .12)',
    // the "pop" CTA glow, blue variant
    easeentrance: 'cubic-bezier(.2, .8, .2, 1)',
    easestandard: 'cubic-bezier(.4, 0, .2, 1)',

    transitionDuration: {
        fast: '0.1s',
        normal: '0.15s',
        slow: '0.3s'
    },

    // Border radius system (preset primitive tokens)
    borderRadius: {
        rounded: '9999px'
    },

    // Responsive spacing system (values emitted at bootstrap — see tokens/spacings.ts)
    spacing: {
        huge: {
            '2': 'var(--spacing-huge-2)',
            '1': 'var(--spacing-huge-1)'
        },
        medium: {
            '5': 'var(--spacing-medium-5)',
            '4': 'var(--spacing-medium-4)',
            '3': 'var(--spacing-medium-3)',
            '2': 'var(--spacing-medium-2)',
            '1': 'var(--spacing-medium-1)'
        },
        small: {
            '3': 'var(--spacing-small-3)',
            '2': 'var(--spacing-small-2)',
            '1': 'var(--spacing-small-1)'
        },
        mainBorder: 'var(--main-border)'
    }
};
